import { ThemeEvents } from './events.js';

const FULL_DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTH_NAMES = [
	'January',
	'February',
	'March',
	'April',
	'May',
	'June',
	'July',
	'August',
	'September',
	'October',
	'November',
	'December',
];

// Beyond this many days out, a day name wraps the week and stops making sense, so show a date instead.
const DAY_NAME_MAX_DAYS = 5;

class DeliveryEtaComponent extends HTMLElement {
	#interval;
	#bankHolidays = [];
	#excludedDays = new Set();
	#excludeBankHolidays = false;
	#cutoffHour = 14;
	#cutoffMinute = 0;
	#deliveryDays = 1;
	#available = true;
	/** Locale strings and locale, set on stores Herald marks distributor. Without them, English as before. */
	#strings = null;
	#locale;

	connectedCallback() {
		if (this.dataset.strings) {
			this.#strings = JSON.parse(this.dataset.strings);
			this.#locale = this.dataset.locale || undefined;
		}
		this.#deliveryDays = parseInt(this.dataset.deliveryDays, 10) || 1;
		this.#cutoffHour = parseInt(this.dataset.cutoffHour, 10) || 0;
		this.#cutoffMinute = parseInt(this.dataset.cutoffMinute, 10) || 0;
		this.#excludeBankHolidays = this.dataset.excludeBankHolidays === 'true';
		this.#available = this.dataset.available !== 'false';

		const days = (this.dataset.excludedDays || '').split(',').filter(Boolean);
		this.#excludedDays = new Set(days.map(Number));

		this.#bindVariantEvents();
		this.#init();
	}

	disconnectedCallback() {
		clearInterval(this.#interval);
	}

	#bindVariantEvents() {
		const section = this.closest('.shopify-section, dialog');
		if (!section) return;

		section.addEventListener(ThemeEvents.variantUpdate, (event) => {
			const available = event.detail.resource?.available ?? false;
			this.#setAvailability(available);
		});
	}

	#setAvailability(available) {
		this.#available = available;
		this.hidden = !available;
		if (available) this.#update();
	}

	async #init() {
		await this.#fetchBankHolidays();
		this.#setAvailability(this.#available);
		this.#update();

		const now = new Date();
		const msToNextMinute = 60000 - (now.getSeconds() * 1000 + now.getMilliseconds());
		setTimeout(() => {
			this.#update();
			this.#interval = setInterval(() => this.#update(), 60000);
		}, msToNextMinute);
	}

	async #fetchBankHolidays() {
		if (!this.#excludeBankHolidays) return;

		const cacheKey = 'uk_bank_holidays';
		const cacheExpiryKey = 'uk_bank_holidays_expiry';

		try {
			const cached = localStorage.getItem(cacheKey);
			const expiry = localStorage.getItem(cacheExpiryKey);

			if (cached && expiry && Date.now() < parseInt(expiry, 10)) {
				this.#bankHolidays = JSON.parse(cached);
				return;
			}

			const res = await fetch('https://www.gov.uk/bank-holidays.json');
			const data = await res.json();
			this.#bankHolidays = (data['england-and-wales']?.events || []).map((e) => e.date);

			localStorage.setItem(cacheKey, JSON.stringify(this.#bankHolidays));
			localStorage.setItem(cacheExpiryKey, String(Date.now() + 86400000));
		} catch {
			this.#bankHolidays = [];
		}
	}

	#isBankHoliday(date) {
		if (!this.#excludeBankHolidays) return false;
		// Local calendar date, like getDay() above. toISOString() is UTC, so in BST it read the previous day before 1am.
		const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
		return this.#bankHolidays.includes(key);
	}

	#isExcluded(date) {
		return this.#excludedDays.has(date.getDay()) || this.#isBankHoliday(date);
	}

	#addBusinessDays(from, days) {
		const d = new Date(from);
		let added = 0;
		while (added < days) {
			d.setDate(d.getDate() + 1);
			if (!this.#isExcluded(d)) added++;
		}
		return d;
	}

	#nextEligible(from) {
		const d = new Date(from);
		while (this.#isExcluded(d)) d.setDate(d.getDate() + 1);
		return d;
	}

	/**
	 * Formats arrival date as "Today", "Tomorrow", a full day name (e.g. "Wednesday"),
	 * or a date (e.g. "5th July") when more than DAY_NAME_MAX_DAYS away.
	 * @param {Date} date
	 * @returns {string}
	 */
	#formatArrivalDate(date) {
		const now = new Date();
		const tomorrow = new Date(now);
		tomorrow.setDate(tomorrow.getDate() + 1);

		if (this.#isSameDay(date, now)) return this.#strings?.today ?? 'Today';
		if (this.#isSameDay(date, tomorrow)) return this.#strings?.tomorrow ?? 'Tomorrow';

		const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
		const arrival = new Date(date.getFullYear(), date.getMonth(), date.getDate());
		const daysAway = Math.round((arrival - today) / 86400000);
		if (this.#strings) {
			const format = daysAway > DAY_NAME_MAX_DAYS ? { day: 'numeric', month: 'long' } : { weekday: 'long' };
			return new Intl.DateTimeFormat(this.#locale, format).format(date);
		}
		if (daysAway > DAY_NAME_MAX_DAYS) return this.#formatLongDate(date);

		return FULL_DAY_NAMES[date.getDay()];
	}

	/**
	 * Formats a date as "5th July".
	 * @param {Date} date
	 * @returns {string}
	 */
	#formatLongDate(date) {
		const day = date.getDate();
		let suffix = 'th';
		if (day % 100 < 11 || day % 100 > 13) {
			if (day % 10 === 1) suffix = 'st';
			else if (day % 10 === 2) suffix = 'nd';
			else if (day % 10 === 3) suffix = 'rd';
		}
		return `${day}${suffix} ${MONTH_NAMES[date.getMonth()]}`;
	}

	/**
	 * @param {Date} a
	 * @param {Date} b
	 * @returns {boolean}
	 */
	#isSameDay(a, b) {
		return (
			a.getDate() === b.getDate() &&
			a.getMonth() === b.getMonth() &&
			a.getFullYear() === b.getFullYear()
		);
	}

	/**
	 * Formats the cutoff time as "2pm" or "2:30pm".
	 * @returns {string}
	 */
	#formatCutoffTime() {
		const h = this.#cutoffHour;
		const m = this.#cutoffMinute;
		const period = h >= 12 ? 'pm' : 'am';
		const hour12 = h % 12 || 12;
		return m > 0 ? `${hour12}:${String(m).padStart(2, '0')}${period}` : `${hour12}${period}`;
	}

	#update() {
		if (!this.#available) return;

		const now = new Date();
		const cutoff = new Date(now);
		cutoff.setHours(this.#cutoffHour, this.#cutoffMinute, 0, 0);

		const orderEl = this.querySelector('.delivery-eta__order-text');
		const arrivalEl = this.querySelector('.delivery-eta__arrival-text');

		if (!orderEl || !arrivalEl) return;

		const canDispatchToday = !this.#isExcluded(now) && now < cutoff;
		let dispatchDate;

		if (canDispatchToday) {
			dispatchDate = new Date(now);
			orderEl.textContent = this.#strings
				? this.#strings.order_by.replace('{time}', new Intl.DateTimeFormat(this.#locale, { hour: 'numeric', minute: '2-digit' }).format(cutoff))
				: `Order by ${this.#formatCutoffTime()}`;
		} else {
			dispatchDate = this.#nextEligible(
				new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1)
			);
			orderEl.textContent = this.#strings?.order_now ?? 'Order now';
		}

		const deliveryDate =
			this.#deliveryDays === 0
				? dispatchDate
				: this.#addBusinessDays(dispatchDate, this.#deliveryDays);

		const arrival = this.#formatArrivalDate(deliveryDate);
		arrivalEl.textContent = this.#strings ? this.#strings.arrives.replace('{date}', arrival) : `Arrives ${arrival}`;
	}
}

if (!customElements.get('delivery-eta-component')) {
	customElements.define('delivery-eta-component', DeliveryEtaComponent);
}
